import dayjs from "dayjs";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import { withTxnBenchmarkFields } from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";
import { FD_DUE_DATE_REPORT } from "./fdReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrFD", "roi"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return null;
  }
  const n = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    row.totalTimeInDays ??
    meta.averageDays ??
    meta.averageDay ??
    meta.days;
  if (val != null && val !== "") return val;

  const depositRaw = row.transactionDate ?? row.trxnDate ?? meta.transactionDate;
  const depositMs = Number(depositRaw);
  if (Number.isFinite(depositMs) && depositMs > 0) {
    const days = dayjs().diff(dayjs(depositMs), "day");
    if (Number.isFinite(days) && days >= 0) return days;
  }
  return "-";
};

const normalizeFdReportTotalHoldingInfo = (
  info: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null => {
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLossPercentage:
      info.gainLossPercentage ?? info.gainLossPerc ?? info.roi,
    cagrFD: info.cagrFD ?? info.cagr,
    cagr: info.cagr ?? info.cagrFD ?? info.roi,
  };
};

const normalizeFdHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const invested =
    row.investedAmount ??
    row.costValue ??
    row.amount ??
    meta.investedAmount;
  const current =
    row.currentAmount ??
    row.currentValue ??
    row.maturityAmount ??
    row.returnAmount ??
    meta.currentAmount;
  const investedNum = toFiniteNumber(invested);
  const currentNum = toFiniteNumber(current);
  const gainLoss =
    row.gainLoss ??
    row.unrealisedGainLoss ??
    row.unrealisedPNL ??
    (investedNum != null && currentNum != null
      ? currentNum - investedNum
      : undefined);
  const gainLossNum = toFiniteNumber(gainLoss);
  let gainLossPercentage =
    row.gainLossPercentage ??
    row.gainLossPerc ??
    meta.gainLossPercentage;
  if (
    (gainLossPercentage == null || gainLossPercentage === "") &&
    investedNum != null &&
    investedNum > 0 &&
    gainLossNum != null
  ) {
    gainLossPercentage = (gainLossNum / investedNum) * 100;
  }
  const depositDateRaw =
    row.transactionDate ?? row.trxnDate ?? meta.transactionDate;

  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName: row.schemeName ?? row.name ?? "-",
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      folioNumber: row.folioNumber ?? row.folioNo ?? row.folio ?? "-",
      transactionDate: formatEpochDate(depositDateRaw),
      maturityDate: formatEpochDate(row.maturityDate ?? meta.maturityDate),
      investedAmount: invested ?? row.investedAmount,
      currentAmount: current ?? row.currentAmount,
      tenure: row.tenure ?? meta.tenure ?? "-",
      roi: row.roi ?? meta.roi ?? "-",
      option: row.option ?? row.fdOption ?? meta.fdOption ?? "-",
      fdStatus: row.fdStatus ?? "-",
      brokerName: row.brokerName ?? meta.brokerName ?? "-",
      gainLoss: gainLoss ?? row.gainLoss ?? "-",
      gainLossPercentage: gainLossPercentage ?? row.gainLossPercentage ?? "-",
      cagr: resolveHoldingCagr(row),
      days: resolveHoldingAverageDays({
        ...row,
        transactionDate: depositDateRaw,
      }),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

const extractFdHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null =>
  normalizeFdReportTotalHoldingInfo(
    (payload?.totalHoldingInfo ??
      payload?.totalHolding ??
      payload?.data?.totalHoldingInfo ??
      null) as Record<string, unknown> | null,
  );

export type ParsedFdReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parseFdReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedFdReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;

  switch (reportType) {
    case "Holding":
    case FD_DUE_DATE_REPORT: {
      const holdings = extractFdHoldings(payload);
      rows = holdings.map(normalizeFdHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) ?? rows.length;
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};
